<template>
  <div class="noa-review">
    <div class="nr-head">
      <button type="button" class="btn btn-clear float-right" @click="$emit('discard')" />
      <span class="nr-eyebrow">Nota da consulta</span>
      <h6>Revisar antes de aplicar</h6>
      <p>{{ helpText }}</p>
    </div>

    <div class="nr-body">
      <div class="empty" v-if="sections.length === 0">
        <p class="empty-title h6">Nada para revisar</p>
        <p class="empty-subtitle">O texto voltou sem conteúdo aproveitável.</p>
      </div>

      <div class="nr-list" v-else>
        <div
          class="nr-item"
          :class="{ selected: section.selected, disabled: !section.target }"
          v-for="(section, i) in sections"
          :key="i"
        >
          <label class="form-checkbox nr-check">
            <input type="checkbox" v-model="section.selected" :disabled="!section.target">
            <i class="form-icon" />
          </label>

          <div class="nr-content">
            <div class="nr-title">{{ section.label }}</div>
            <div class="nr-text" v-html="section.html" />

            <div class="nr-target">
              <template v-if="targets.length > 1">
                <span class="nr-target-label">Aplicar em</span>
                <select
                  class="form-select select-sm"
                  v-model="section.target"
                  @change="onTargetChange(section)"
                >
                  <option value="">Não aplicar</option>
                  <option v-for="field in targets" :key="field.name" :value="field.name">
                    {{ field.label }}
                  </option>
                </select>
              </template>

              <div class="nr-mode" v-if="section.target">
                <label class="form-radio form-inline">
                  <input type="radio" :name="`mode-${i}`" value="append" v-model="section.mode">
                  <i class="form-icon" />Acrescentar
                </label>
                <label class="form-radio form-inline">
                  <input type="radio" :name="`mode-${i}`" value="replace" v-model="section.mode">
                  <i class="form-icon" />Substituir
                </label>
              </div>
            </div>

            <div class="nr-warning" v-if="isOverwriting(section)">
              <fa-icon :icon="['fal', 'exclamation-triangle']" />
              <span>"{{ targetLabel(section.target) }}" já tem conteúdo e será substituído.</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="nr-foot" v-if="sections.length > 0">
      <button type="button" class="btn btn-link" @click="toggleAll">
        {{ allSelected ? 'Desmarcar todos' : 'Selecionar todos' }}
      </button>
      <div class="nr-foot-actions">
        <button type="button" class="btn" @click="$emit('discard')">Fechar</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="selectedSections.length === 0"
          @click="apply"
        >
          Aplicar {{ selectedSections.length }}
          {{ selectedSections.length === 1 ? 'trecho' : 'trechos' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { toText } from '@/helpers/html';

const TARGET_TYPES = ['editor', 'text'];

// templates antigos guardam o campo sem rótulo: o nome técnico não diz nada ao profissional
const FIELD_LABELS = {
  content: 'Evolução',
  evolution: 'Evolução',
  mainComplaint: 'Queixa principal',
  subjective: 'Subjetivo',
  objective: 'Objetivo',
  physicalExam: 'Exame físico',
  diagnosis: 'Diagnóstico',
  assessment: 'Avaliação',
  hypothesis: 'Hipótese diagnóstica',
  conduct: 'Conduta',
  plan: 'Plano',
  observations: 'Observações',
  notes: 'Anotações',
};

const SYNONYMS = [
  { names: ['mainComplaint'], terms: ['queixa principal', 'queixa', 'motivo da consulta', 'qp'] },
  {
    names: ['subjective', 'medicalHistory', 'history'],
    terms: [
      'historia da doenca atual', 'hda', 'historia clinica', 'historia',
      'subjetivo', 'anamnese', 'antecedentes', 'historia pregressa',
    ],
  },
  {
    names: ['objective', 'physicalExam', 'exam'],
    terms: ['exame fisico', 'objetivo', 'exame', 'achados'],
  },
  {
    names: ['diagnosis', 'assessment', 'hypothesis'],
    terms: ['hipotese diagnostica', 'hipoteses', 'diagnostico', 'avaliacao', 'impressao'],
  },
  {
    names: ['conduct', 'plan', 'treatment'],
    terms: ['conduta', 'plano', 'plano terapeutico', 'tratamento', 'orientacoes', 'prescricao'],
  },
  {
    names: ['observations', 'notes'],
    terms: ['observacoes', 'observacao', 'notas', 'comentarios'],
  },
  {
    names: ['content', 'evolution'],
    terms: ['evolucao', 'resumo', 'consulta', 'registro'],
  },
];

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function toParagraphs(text) {
  return String(text || '')
    .split(/\n{2,}|\n/)
    .map(block => block.trim())
    .filter(Boolean)
    .map((block) => {
      const paragraph = document.createElement('p');
      paragraph.textContent = block;
      return paragraph.outerHTML;
    })
    .join('');
}

function parseContent(content) {
  if (!content) {
    return [];
  }

  if (typeof content === 'object') {
    return Object.entries(content)
      .filter(([, value]) => typeof value === 'string' && value.trim())
      .map(([label, value]) => ({ label: label.trim(), text: value.trim() }));
  }

  const value = String(content).trim();

  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    if (parsed && typeof parsed === 'object') {
      return parseContent(parsed);
    }
  } catch (error) {
    return [{ label: 'Resumo da consulta', text: value }];
  }

  return [{ label: 'Resumo da consulta', text: value }];
}

export default {
  props: {
    summary: {
      type: Object,
      default: null,
    },
    inputs: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      sections: [],
    };
  },
  mounted() {
    this.build();
  },
  watch: {
    summary() {
      this.build();
    },
  },
  computed: {
    targets() {
      return this.inputs
        .filter(input => TARGET_TYPES.includes(input.type))
        .map(input => ({
          name: input.name,
          label: input.label || FIELD_LABELS[input.name] || input.name,
        }));
    },
    helpText() {
      if (this.targets.length > 1) {
        return 'Texto gerado por IA a partir da gravação. Confira cada trecho, escolha o'
          + ' campo de destino e aplique só o que estiver correto.';
      }

      return 'Texto gerado por IA a partir da gravação. Marque os trechos corretos e escolha'
        + ' se cada um acrescenta ou substitui o que já está escrito.';
    },
    selectedSections() {
      return this.sections.filter(section => section.selected && section.target);
    },
    allSelected() {
      return this.sections.length > 0
        && this.sections.every(section => section.selected || !section.target);
    },
  },
  methods: {
    build() {
      const used = new Set();
      // com um único campo de destino, todo trecho vai para ele, um após o outro
      const single = this.targets.length === 1 ? this.targets[0].name : null;
      let first = true;

      this.sections = parseContent(this.summary && this.summary.content)
        .map((section) => {
          const target = single || this.matchTarget(section.label, used);

          if (target && !single) {
            used.add(target);
          }

          const replace = first && !this.hasContent(target);
          first = false;

          return {
            label: section.label,
            text: section.text,
            html: toParagraphs(section.text),
            target,
            mode: replace ? 'replace' : 'append',
            selected: !!target,
          };
        });
    },
    matchTarget(label, used) {
      const term = normalize(label);
      const available = this.targets.filter(({ name }) => !used.has(name));

      if (!term || available.length === 0) {
        return '';
      }

      const exact = available.find(({ label: name }) => normalize(name) === term);

      if (exact) {
        return exact.name;
      }

      const synonym = SYNONYMS.find(({ terms }) => terms.some(item => (
        term === item || term.includes(item) || item.includes(term)
      )));

      if (synonym) {
        const byName = available.find(({ name }) => synonym.names.includes(name));

        if (byName) {
          return byName.name;
        }

        const byLabel = available.find(({ label: name }) => synonym.terms.some((item) => {
          const target = normalize(name);
          return target === item || target.includes(item);
        }));

        if (byLabel) {
          return byLabel.name;
        }
      }

      return '';
    },
    findInput(name) {
      return this.inputs.find(input => input.name === name);
    },
    hasContent(name) {
      const input = this.findInput(name);
      return !!input && !!toText(input.value || '').trim();
    },
    targetLabel(name) {
      const target = this.targets.find(item => item.name === name);
      return target ? target.label : '';
    },
    isOverwriting(section) {
      return section.selected && section.mode === 'replace' && this.hasContent(section.target);
    },
    onTargetChange(section) {
      section.selected = !!section.target;
      section.mode = this.hasContent(section.target) ? 'append' : 'replace';
    },
    toggleAll() {
      const selected = !this.allSelected;
      this.sections.forEach((section) => {
        section.selected = selected && !!section.target;
      });
    },
    /**
     * Vários trechos caem no mesmo campo quando o template tem um destino só:
     * o título de cada um separa o que veio de onde. Quando o campo já se chama
     * como o trecho, o título seria repetição e fica de fora.
     */
    withTitle(section) {
      return normalize(section.label) !== normalize(this.targetLabel(section.target));
    },
    apply() {
      this.$emit('apply', this.selectedSections.map((section) => {
        const title = this.withTitle(section);

        return {
          target: section.target,
          label: section.label,
          text: title ? `${section.label}\n${section.text}` : section.text,
          html: title
            ? `<p><strong>${section.label}</strong></p>${section.html}`
            : section.html,
          mode: section.mode,
        };
      }));
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.modal-container.nr-modal {
  padding: 0;
}

.noa-review {
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  .nr-head {
    border-bottom: $border-width solid $border-color;
    padding: $layout-spacing-lg;

    .nr-eyebrow {
      color: $primary-color;
      font-size: $font-size-xs;
      font-weight: 700;
      letter-spacing: .05rem;
      text-transform: uppercase;
    }

    h6 {
      color: $gray-color-900;
      font-weight: 700;
      margin: $unit-1 0;
    }

    p {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin: 0;
      max-width: 36rem;
    }
  }

  .nr-body {
    flex: 1;
    overflow-y: auto;
    padding: $layout-spacing-lg;
  }

  .nr-list {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
  }

  .nr-item {
    display: flex;
    gap: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;

    & + .nr-item {
      border-top: $border-width solid $border-color;
    }

    &.selected {
      background: lighten($primary-color, 36%);
    }

    &.disabled .nr-title {
      color: $gray-color-dark;
    }
  }

  .nr-check {
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
    width: .8rem;
  }

  .nr-content {
    flex: 1;
    min-width: 0;
  }

  .nr-title {
    color: $gray-color-900;
    font-weight: 700;
    margin-bottom: $unit-1;
  }

  .nr-text {
    color: $gray-color-900;
    line-height: 1.5;

    p {
      margin: 0 0 $unit-1;
    }
  }

  .nr-target {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing;
    margin-top: $unit-2;

    .nr-target-label {
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }

    .form-select {
      max-width: 12rem;
    }
  }

  .nr-mode {
    display: flex;
    gap: $layout-spacing-lg;

    .form-radio {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin: 0;
      padding-left: 1.1rem;
    }
  }

  .nr-warning {
    align-items: center;
    color: #8A6B10;
    display: flex;
    font-size: $font-size-sm;
    gap: $unit-2;
    margin-top: $unit-2;
  }

  .nr-foot {
    align-items: center;
    border-top: $border-width solid $border-color;
    display: flex;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;

    .btn-link:focus {
      box-shadow: none;
    }
  }

  .nr-foot-actions {
    display: flex;
    gap: $layout-spacing;
  }
}
</style>
